import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'app/hooks';
import { projectBoardApi } from 'app/services/projectBoardApi';
import {
  removeBusyAhu,
  setAhuRightPreloader,
} from 'app/slices/projectBoardSlice';
import { store } from 'app/store';
import { getSocket, type SocketDataMessage } from 'app/socketClient';

/**
 * AHU-list socket handling (B1.8) — the panel's own `'data'` listener on the
 * shared socket, additive to the dashboard's. This is the ONLY completion
 * path for ADD / Duplicate / Create Version (OQ B-1): the graceful no-socket
 * path is the documented legacy behavior — the list silently does not
 * refresh (NO invented polling).
 *
 *  - `duplicate_status` (any value) → `onDuplicateStatus(success)` tears down
 *    the in-flight duplicate UI state — re-entry guard, watchdog, spinner —
 *    on the panel (KA-5238); `'success'` + unit_id in the cached list
 *    additionally refetches and re-selects the clone's row (parity 147/152).
 *  - `model 'ahuunit'` code `delete`: own delete (busy set) → just clear the
 *    busy id; foreign delete → silent refetch (parity 152).
 *  - code `post`: after 500ms silent refetch → 500ms right preloader, select
 *    the new row (`duplicatedModelId` fallback), scroll the list to the
 *    bottom (B1.8 / parity 137).
 *  - code `put`: the generic stateStorage patcher (Part A §1.8) — in-place
 *    row update (live rename/qty edits from another window) (parity 152).
 *
 * [NEG] honored: the `duplicate_static_version_status` branch feeds only the
 * dead static-versions sub-list (B1.9 / parity 154) — not built.
 */
export function useAhuSocket(
  projectId: number,
  onSelectRow: (ahuId: number) => void,
  onDuplicateStatus?: (success: boolean) => void
): void {
  const dispatch = useAppDispatch();
  const selectRowRef = useRef(onSelectRow);
  selectRowRef.current = onSelectRow;
  const duplicateStatusRef = useRef(onDuplicateStatus);
  duplicateStatusRef.current = onDuplicateStatus;

  useEffect(() => {
    if (!Number.isFinite(projectId)) return undefined;
    const socket = getSocket();
    const timers: ReturnType<typeof setTimeout>[] = [];

    const cachedUnits = () =>
      projectBoardApi.endpoints.getAhuUnits.select(projectId)(store.getState())
        .data;

    const refetchUnits = () =>
      dispatch(
        projectBoardApi.endpoints.getAhuUnits.initiate(projectId, {
          forceRefetch: true,
        })
      ).unwrap();

    const scrollListToBottom = () => {
      timers.push(
        setTimeout(() => {
          const el = document.querySelector(
            '#main_ahus .left-column-proposal-scrollable'
          );
          if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }, 120)
      );
    };

    const onData = (msg: SocketDataMessage) => {
      const data = (msg?.data ?? {}) as Record<string, unknown>;

      // ---- Duplicate completion (any duplicate_status, KA-5238) ----
      if (data.duplicate_status) {
        const success = data.duplicate_status === 'success';
        // Any completion report — success or failure — releases the panel's
        // re-entry guard / watchdog / spinner (Backbone _finishDuplicate).
        duplicateStatusRef.current?.(success);
        if (success) {
          const unitId = Number(data.unit_id);
          if (cachedUnits()?.some((u) => u.id === unitId)) {
            refetchUnits()
              .then(() => {
                // ahuUnitsRender clears the action-time right preloader.
                dispatch(setAhuRightPreloader(false));
                selectRowRef.current(unitId);
              })
              .catch(() => undefined);
          }
        }
      }

      if (msg?.model !== 'ahuunit') return;

      if (msg.code === 'delete') {
        const id = Number(data.id ?? msg.id);
        const busy = store.getState().projectBoard.ahus.busyAhuIds;
        if (busy.includes(id)) {
          // Own delete — the cache row was already removed; just clear the
          // busy marker (suppress the echo refetch).
          dispatch(removeBusyAhu(id));
        } else {
          // Another window deleted an AHU → silent refetch.
          refetchUnits().catch(() => undefined);
        }
      } else if (msg.code === 'post') {
        // ADD / Duplicate / Create Version completion (B1.8).
        const newId = Number(data.id ?? msg.id);
        timers.push(
          setTimeout(() => {
            refetchUnits()
              .then((units) => {
                dispatch(setAhuRightPreloader(true));
                timers.push(
                  setTimeout(() => dispatch(setAhuRightPreloader(false)), 500)
                );
                const fallback =
                  store.getState().projectBoard.ahus.duplicatedModelId;
                const target = units.some((u) => u.id === newId)
                  ? newId
                  : fallback != null && units.some((u) => u.id === fallback)
                    ? fallback
                    : null;
                if (target != null) {
                  selectRowRef.current(target);
                  scrollListToBottom();
                }
              })
              .catch(() => undefined);
          }, 500)
        );
      } else if (msg.code === 'put') {
        // Generic stateStorage put patcher: in-place row update.
        const id = Number(data.id ?? msg.id);
        if (Number.isFinite(id)) {
          dispatch(
            projectBoardApi.util.updateQueryData('getAhuUnits', projectId, (draft) => {
              const row = draft.find((u) => u.id === id);
              if (row) Object.assign(row, data);
            })
          );
        }
      }
    };

    socket.on('data', onData);
    return () => {
      socket.off('data', onData);
      timers.forEach(clearTimeout);
    };
  }, [projectId, dispatch]);
}
